/**
 * Keeps the screen on during a voice call (a locked phone stops the microphone
 * and the call silently dies). The browser drops the lock whenever the page is
 * hidden, so it is taken again when the page comes back.
 */

interface WakeLockSentinelLike {
  released: boolean;
  release(): Promise<void>;
  addEventListener?(type: 'release', listener: () => void): void;
}

export interface WakeLockEnv {
  navigator?: { wakeLock?: { request(type: 'screen'): Promise<WakeLockSentinelLike> } };
  document?: Pick<Document, 'addEventListener' | 'removeEventListener' | 'visibilityState'>;
}

export interface WakeLockHandle {
  acquire(): Promise<void>;
  release(): Promise<void>;
}

export function createWakeLock(env: WakeLockEnv = { navigator, document }): WakeLockHandle {
  let sentinel: WakeLockSentinelLike | null = null;
  let wanted = false;

  const take = async () => {
    const api = env.navigator?.wakeLock;
    if (!api || !wanted || (sentinel && !sentinel.released)) return;
    try {
      sentinel = await api.request('screen');
    } catch {
      sentinel = null; // denied (battery saver) — the call still works, the screen may dim
    }
  };

  const onVisibility = () => {
    if (env.document?.visibilityState === 'visible') void take();
  };

  return {
    async acquire() {
      if (wanted) return;
      wanted = true;
      env.document?.addEventListener('visibilitychange', onVisibility);
      await take();
    },
    async release() {
      wanted = false;
      env.document?.removeEventListener('visibilitychange', onVisibility);
      const current = sentinel;
      sentinel = null;
      try {
        await current?.release();
      } catch {
        /* already released by the browser */
      }
    },
  };
}
